/**
 * usePushNotifications
 * Gestiona el registro del SW, solicitud de permiso y suscripción push.
 * Guarda la suscripción en el backend (/api/push/subscribe).
 */
import { useState, useEffect, useCallback } from "react";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function usePushNotifications(uid) {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [swReady, setSwReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isSupported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    !!VAPID_PUBLIC_KEY;

  // Registrar SW al montar + auto-suscribir si permiso ya concedido
  useEffect(() => {
    if (!isSupported) return;
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        setSwReady(true);
        return reg.pushManager.getSubscription().then(async (existingSub) => {
          if (existingSub) {
            // Ya hay suscripción activa en el browser
            setSubscribed(true);
            return;
          }
          // Permiso ya concedido pero sin suscripción → auto-suscribir sin pedir permiso
          if (Notification.permission === "granted" && uid) {
            try {
              const newSub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
              });
              await fetch(`${BACKEND_URL}/api/push/subscribe`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  uid,
                  subscription: newSub.toJSON(),
                  deviceId: navigator.userAgent.slice(0, 80),
                }),
              });
              setSubscribed(true);
              setPermission("granted");
              console.log("[Push] ✅ Auto-suscripción completada (permiso previo detectado)");
            } catch (err) {
              console.warn("[Push] Auto-suscripción falló:", err.message);
            }
          }
        });
      })
      .catch((err) => console.warn("[Push] SW registration failed:", err));
  }, [isSupported, uid]);

  // Solicitar permiso y suscribirse
  const subscribe = useCallback(async () => {
    if (!isSupported || !uid || !swReady) return;
    setLoading(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setLoading(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      const subJson = sub.toJSON();
      await fetch(`${BACKEND_URL}/api/push/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid,
          subscription: subJson,
          deviceId: navigator.userAgent.slice(0, 80),
        }),
      });
      setSubscribed(true);
      console.log("[Push] ✅ Suscripción registrada");
    } catch (err) {
      console.error("[Push] subscribe error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isSupported, uid, swReady]);

  // Desuscribirse
  const unsubscribe = useCallback(async () => {
    if (!isSupported || !uid) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await sub.unsubscribe();
      await fetch(
        `${BACKEND_URL}/api/push/subscribe?uid=${uid}&endpoint=${encodeURIComponent(sub.endpoint)}`,
        { method: "DELETE" }
      );
    }
    setSubscribed(false);
  }, [isSupported, uid]);

  return { isSupported, permission, subscribed, swReady, loading, error, subscribe, unsubscribe };
}
